import type { SceneKeyframe } from '../scene-clock'

/**
 * Timeline and demo data for the "Who Else Has Your Bank Statements?" scene
 * (the reference's #stmtviz). Pure, unit tested; BankStatementScene renders
 * from what is here.
 *
 * One 9.5 s loop:
 *   statement  the emailed PDF shows every transaction
 *   scan       a lavender band sweeps the statement (1.3 s)
 *   redacted   only income and rent rows keep their text, the rest become
 *              grey bars, the account line blurs, Flinks appears and the mode
 *              pill swaps to the TrueTenant wordmark
 *   shared     the statement dissolves and the landlord's view (income chart
 *              and rent checks) rises in its place
 *
 * The reference enters "redacted" and dissolves the statement in the same
 * instant, so its redaction is never seen. We hold it for a beat first.
 */
export type BankPhase = 'statement' | 'scan' | 'redacted' | 'shared'

export const BANK_LOOP_MS = 9500
export const BANK_SCAN_MS = 1300
export const BANK_KEYFRAMES: readonly SceneKeyframe<BankPhase>[] = [
	{ at: 0, phase: 'statement' },
	{ at: 1800, phase: 'scan' },
	// The reference redacts and dissolves in the same instant (its `red` class
	// drives both), so 'redacted' is never a phase of its own.
	{ at: 2350, phase: 'shared' },
]
export const BANK_FINAL_PHASE: BankPhase = 'shared'

const PHASE_ORDER: readonly BankPhase[] = [
	'statement',
	'scan',
	'redacted',
	'shared',
]

/** True once the timeline has reached `target` (phases are cumulative). */
export function hasReached(phase: BankPhase, target: BankPhase) {
	return PHASE_ORDER.indexOf(phase) >= PHASE_ORDER.indexOf(target)
}

export interface BankSceneState {
	/** The scan band has started its sweep. */
	scanning: boolean
	/** Non-shared rows are grey bars; the pill shows the wordmark. */
	redacted: boolean
	/** The statement has dissolved and the landlord's view is on top. */
	shared: boolean
}

/** What the card draws in a phase. Each flag stays on for the later phases. */
export function bankSceneState(phase: BankPhase): BankSceneState {
	return {
		scanning: hasReached(phase, 'scan'),
		redacted: hasReached(phase, 'redacted'),
		shared: hasReached(phase, 'shared'),
	}
}

/* ---------- Phase A: the emailed statement ---------- */

export type TransactionKind = 'income' | 'rent' | 'other'

export interface StatementRow {
	date: string
	label: string
	amount: string
	kind: TransactionKind
}

/** Income and rent are what the landlord gets; everything else is redacted. */
export function isSharedRow(row: Pick<StatementRow, 'kind'>) {
	return row.kind !== 'other'
}

export const STATEMENT_ROWS: readonly StatementRow[] = [
	{
		date: 'Aug 01',
		label: 'Payroll Deposit',
		amount: '$2,840.00',
		kind: 'income',
	},
	{
		date: 'Aug 01',
		label: 'Rent e-Transfer',
		amount: '$2,100.00',
		kind: 'rent',
	},
	{ date: 'Aug 03', label: 'Grocery Store', amount: '$86.42', kind: 'other' },
	{ date: 'Aug 06', label: 'Food Delivery', amount: '$31.18', kind: 'other' },
	{ date: 'Aug 09', label: 'Pharmacy', amount: '$24.60', kind: 'other' },
	{
		date: 'Aug 12',
		label: 'Transfer to Savings',
		amount: '$400.00',
		kind: 'other',
	},
	{
		date: 'Aug 15',
		label: 'Payroll Deposit',
		amount: '$2,840.00',
		kind: 'income',
	},
	{
		date: 'Aug 21',
		label: 'Streaming Subscription',
		amount: '$18.99',
		kind: 'other',
	},
]

/** Rows turn grey one after another, top to bottom, as the band passes. */
export const REDACT_STEP_MS = 0
export function redactDelayMs(rowIndex: number) {
	return rowIndex * REDACT_STEP_MS
}

/* ---------- Phase B: what the landlord sees ---------- */

export interface IncomeBar {
	/** Month of the lease year, September to August; the chart shows its initial. */
	month: string
	/** Height as a percentage of the chart. */
	percent: number
}

export const INCOME_BARS: readonly IncomeBar[] = [
	{ month: 'Sep', percent: 69 },
	{ month: 'Oct', percent: 71 },
	{ month: 'Nov', percent: 70.1 },
	{ month: 'Dec', percent: 71.5 },
	{ month: 'Jan', percent: 71 },
	{ month: 'Feb', percent: 98.8 },
	{ month: 'Mar', percent: 70.5 },
	{ month: 'Apr', percent: 71.2 },
	{ month: 'May', percent: 69.9 },
	{ month: 'Jun', percent: 71 },
	{ month: 'Jul', percent: 95 },
	{ month: 'Aug', percent: 70.6 },
]

/** The dashed "declared" line, as a percentage of the chart height. */
export const DECLARED_PERCENT = 71

export const RENT_MONTHS = [
	'Sep',
	'Oct',
	'Nov',
	'Dec',
	'Jan',
	'Feb',
	'Mar',
	'Apr',
	'May',
	'Jun',
	'Jul',
	'Aug',
] as const

/** Bars rise 350 ms after the view appears, 50 ms apart. */
export const BAR_DELAY = { baseMs: 350, stepMs: 50 } as const
export function barDelayMs(index: number) {
	return BAR_DELAY.baseMs + index * BAR_DELAY.stepMs
}

/** Checks pop 900 ms after the view appears, 60 ms apart. */
export const CHECK_DELAY = { baseMs: 900, stepMs: 60 } as const
export function checkDelayMs(index: number) {
	return CHECK_DELAY.baseMs + index * CHECK_DELAY.stepMs
}

/** Demo copy for the card. Scene data stays here, not in content.ts. */
export const BANK_COPY = {
	accountKind: 'Chequing Account',
	title: 'August Statement',
	mode: 'Emailed PDF',
	footer: 'Account 0042-118-4821 · Balance $6,214.57',
	income: 'Income',
	declared: 'Declared $5,680',
	rent: 'Rent',
	rentSummary: '$2,100 / Month · Paid On Time',
	flinks: 'Flinks',
} as const
